import PortfolioSelection from '@components/portfolioSelection';
import { Box, Breadcrumbs, Container, Divider, Grid, Typography } from '@mui/material';
import strings from '@src/app/strings/LocalisedStrings';
import Colors from '@src/app/config/colorConfig';
import { holdingsUrl, homeUrl } from '@src/app/utils/UrlBuilder';
import { FC, useEffect, useMemo, useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router';
import HoldingSecurityDetails from './HoldingSecurityDetails';
import TransactionsDataGrid from '../../transactions/TransactionsDataGrid';
import { useWindow } from '@src/app/hooks/useWindow';
import { useAppDispatch, useAppSelector } from '@src/app/hooks/store';
import { selectHoldings } from '@src/app/store/reducers/holdings/selectors';
import { useHoldingsPolling } from '@src/app/hooks/useHoldingsPolling';
import { selectPortfolios } from '@src/app/store/reducers/portfolios/selectors';
import { DateTime } from 'luxon';
import { ScreenState } from '@src/app/enums';
import { PageContent } from '@components/index';
import { getTransactionsAsync } from '@src/app/store/reducers/transactions/thunks';
import { Holding } from 'clientportalshared';
import { selectTransactions } from '@src/app/store/reducers/transactions/selectors';
import { setSecurityAsOfDate } from '@src/app/store/reducers/holdings';
import useQuery from '@src/app/hooks/useQuery';
import urlParamKeys from '@src/app/constants/urlParamKeys';
import AdapterLuxon from '@mui/lab/AdapterLuxon';
import { LocalizationProvider } from '@mui/lab';
import DateRangePicker from '@components/dateRange';
import { DateRange } from '@src/app/store';
import { Home } from '@mui/icons-material';

const HoldingsSecurity: FC = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const { portfoliosSelected, portfolioInfoState, earliestInception } = useAppSelector(selectPortfolios);
  const { state: transactionState, data: transactionData } = useAppSelector(selectTransactions);
  const { data, state: holdingState, securityAsOfDate } = useAppSelector(selectHoldings);

  const { fetchHoldings } = useHoldingsPolling();

  const query = useQuery();
  const dateAsOf = query.get(urlParamKeys.dateAsOf);
  const history = useHistory();

  const [currentPageSize, setCurrentPageSize] = useState<number>(25);

  const { securityCode } = useParams<{ securityCode: string }>();
  const { resizing } = useWindow();

  const setAsAtDate = (date: DateRange) => {
    dispatch(setSecurityAsOfDate(date));
  };

  const fetchHoldingsData = async () => {
    if (portfolioInfoState !== ScreenState.SuccessDataLoaded || portfoliosSelected.length === 0) {
      return;
    }

    const date = dateAsOf ?? DateTime.now().toISODate();
    await fetchHoldings({ asOfDate: date, clientPortfolios: portfoliosSelected, securityCode: securityCode });
  };

  const fetchTransactions = async () => {
    await dispatch(
      getTransactionsAsync({
        securityCode: securityCode,
        fromDate: securityAsOfDate.dateFrom,
        toDate: securityAsOfDate.dateTo,
        clientPortfolios: portfoliosSelected,
      })
    );
  };

  useEffect(() => {
    dispatch(
      setSecurityAsOfDate({
        dateFrom: earliestInception,
        dateTo: dateAsOf ?? DateTime.now().toISODate(),
      })
    );
  }, []);

  useEffect(() => {
    fetchHoldingsData();
  }, [portfoliosSelected, portfolioInfoState]);

  useEffect(() => {
    if (securityAsOfDate.dateFrom.length === 0 || securityAsOfDate.dateTo.length === 0) {
      return;
    }
    fetchTransactions();
  }, [portfoliosSelected, securityAsOfDate]);

  const holdingSecurity: Holding | undefined = useMemo(() => {
    return data?.holdingList?.data?.[0];
  }, [data]);

  if (!dateAsOf || !securityCode || securityCode.length < 1) {
    return <Redirect to={holdingsUrl()} />;
  }
  const transactions = transactionData?.transactions ?? [];
  const transactionsLoading = transactionState === ScreenState.Loading;
  const loading = portfolioInfoState === ScreenState.Loading || holdingState === ScreenState.Loading || transactionsLoading;
  return (
    <Container maxWidth={false} sx={{ marginBottom: '24px' }}>
      <Box display="flex" gap="24px" mb={6} justifyContent="space-between" mt={2} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
        <Breadcrumbs>
          <Typography
            onClick={() => history.push(homeUrl())}
            sx={{
              display: 'flex',
              alignItems: 'center',
              ':hover': {
                cursor: 'pointer',
              },
            }}
            color="inherit"
          >
            <Home sx={{ mr: 0.5 }} fontSize="small" />
            Dashboard
          </Typography>
          <Typography fontWeight={600} fontSize="20px" color="#222222">
            Holdings
          </Typography>
        </Breadcrumbs>

        <Box display="flex" alignItems="center" gap="16px">
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DateRangePicker
              inceptionDate={earliestInception}
              dateRange={securityAsOfDate}
              setDateRange={(dateRange: DateRange) => setAsAtDate(dateRange)}
            />
          </LocalizationProvider>
          <PortfolioSelection allowCombining={false} disabled={loading} />
        </Box>
      </Box>
      <PageContent isLoading={loading}>
        <Grid container direction="column" spacing={2}>
          <HoldingSecurityDetails
            holdingsDate={dateAsOf}
            price={holdingSecurity?.price ?? 0}
            units={holdingSecurity?.units ?? 0}
            priceDate={holdingSecurity?.priceDate ?? DateTime.now().toISODate()}
            value={holdingSecurity?.value ?? 0}
            costBase={holdingSecurity?.costBase ?? 0}
            estimatedGainOrLoss={holdingSecurity?.estimatedGainOrLoss ?? 0}
            percentageOfPortfolio={holdingSecurity?.percentageOfPortfolio ?? 0}
            totalReturnValue={holdingSecurity?.totalReturnValue ?? 0}
            securityCode={securityCode}
            securityName={holdingSecurity?.securityName ?? 'Security name not found'}
          />
          <Grid item>
            <Divider />
          </Grid>
          <Grid item>
            <Typography fontWeight={700} fontSize={20} color={Colors.gray800}>
              {strings.allTransactions}
            </Typography>
          </Grid>

          {!resizing && (
            <Grid item>
              <TransactionsDataGrid
                currentPageSize={currentPageSize}
                setCurrentPageSize={setCurrentPageSize}
                hideSecurity
                isLoading={transactionsLoading}
                data={transactions}
              />
            </Grid>
          )}
        </Grid>
      </PageContent>
    </Container>
  );
};

export default HoldingsSecurity;
